import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import {
  FEEDBACK_AGAIN,
  FEEDBACK_DID_NOT_COME,
  FEEDBACK_FEEL,
  feedbackEvents,
  ONLINE_DAYS,
  ONLINE_INTEREST,
  ONLINE_TIME_IDS,
  ONLINE_TIME_LABEL,
} from "@repo/vihangam-yog-shared";
import type { inferRouterOutputs } from "@trpc/server";
import { Check, Copy, Download } from "lucide-react";
import { useMemo, useState } from "react";

import { CHAPTER } from "../chapter";
import { downloadCsv, SearchField, StatPlate } from "../components/DataKit";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

type Data = inferRouterOutputs<AppRouter>["adminEventFeedback"];
type Response = Data["responses"][number];

const EVENT_LABEL = new Map<string, string>([
  ...feedbackEvents().map((e): [string, string] => [
    e.slug,
    `${e.title}, ${e.dateLabel.replace(/\s\d{4}$/, "")}`,
  ]),
  [FEEDBACK_DID_NOT_COME, "Registered but couldn't make it"],
]);

const buttonClass =
  "inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] px-5 text-label font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function wantsOnline(r: Response): boolean {
  return r.answers.online === "Yes" || r.answers.online === "Maybe";
}

/** "Tue, Thu · Evening, Later evening", or "" when they ticked nothing. */
function onlineWhen(r: Response): string {
  const days = r.answers.days.join(", ");
  const times = r.answers.times
    .map((t) => ONLINE_TIME_LABEL[t].label)
    .join(", ");
  return [days, times].filter(Boolean).join(" · ");
}

/** One row of counts as a labelled bar, so a glance shows the spread. */
function CountRow({
  label,
  count,
  of,
}: {
  label: string;
  count: number;
  of: number;
}) {
  const share = of > 0 ? Math.round((count / of) * 100) : 0;
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_3rem] items-center gap-3">
      <div>
        <p className="text-body text-[color:var(--color-ink)]">{label}</p>
        <div
          aria-hidden="true"
          className="mt-1.5 h-1.5 rounded-full bg-[color:var(--color-line)]"
        >
          <div
            className="h-full rounded-full bg-[color:var(--color-amber)]"
            style={{ width: `${share}%` }}
          />
        </div>
      </div>
      <p className="text-right text-lead font-semibold tabular-nums text-[color:var(--color-ink)]">
        {count}
      </p>
    </li>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="surface p-5 md:p-6">
      <h2 className="font-display text-lead text-[color:var(--color-ink)]">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/**
 * Event feedback: the answers to the form after the September 2026 visit
 * ({siteHost}/feedback). The tallies count each person's latest answer;
 * the online meditation grid shows how many interested people ticked
 * each day and time, to choose when to run it. Organisers only.
 */
export function EventFeedback() {
  const query = trpc.adminEventFeedback.useQuery(undefined, {
    retry: false,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null);

  const responses = useMemo(() => query.data?.responses ?? [], [query.data]);
  const latest = useMemo(
    () => responses.filter((r) => !r.superseded),
    [responses],
  );
  const interested = useMemo(() => latest.filter(wantsOnline), [latest]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return responses;
    }
    return responses.filter((r) =>
      [r.name, r.email, r.answers.stayed, r.answers.better, r.answers.more]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [responses, search]);

  async function copyInterested() {
    try {
      await navigator.clipboard.writeText(
        interested.map((r) => r.email).join(", "),
      );
      setCopied("ok");
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      // Clipboard blocked: say so, or they paste nothing and don't know why.
      setCopied("failed");
      window.setTimeout(() => setCopied(null), 6000);
    }
  }

  function download() {
    downloadCsv(
      "event-feedback-september-2026.csv",
      [
        "Sent",
        "Name",
        "Email",
        "Events",
        "How it was",
        "What stayed with them",
        "What we could do better",
        "Come again",
        "Online meditation",
        "Days",
        "Times",
        "Anything else",
        "Later answer from them",
      ],
      responses.map((r) => [
        r.createdAt,
        r.name,
        r.email,
        r.answers.events.map((e) => EVENT_LABEL.get(e) ?? e).join("; "),
        r.answers.feel ?? "",
        r.answers.stayed,
        r.answers.better,
        r.answers.again ?? "",
        r.answers.online ?? "",
        r.answers.days.join(" "),
        r.answers.times.map((t) => ONLINE_TIME_LABEL[t].label).join("; "),
        r.answers.more,
        r.superseded ? "yes" : "",
      ]),
    );
  }

  const summary = query.data?.summary;
  const gridMax = summary
    ? Math.max(
        1,
        ...ONLINE_DAYS.flatMap((d) =>
          ONLINE_TIME_IDS.map((t) => summary.grid[d]?.[t] ?? 0),
        ),
      )
    : 1;

  return (
    <section className="mx-auto max-w-[1100px] px-5 py-10 md:px-10 md:py-12">
      <header className="border-b border-[color:var(--color-line)] pb-6">
        <h1 className="page-title">Feedback on the September events</h1>
        <p className="mt-3 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
          Answers to the form at {CHAPTER.siteHost}/feedback, which the
          follow-up email links to. If someone answers twice, their latest
          answer is the one counted.
        </p>
      </header>

      {query.isLoading ? (
        <p className="mt-8 text-body text-[color:var(--color-ink-soft)]">
          Loading answers…
        </p>
      ) : null}

      {query.error ? (
        <p className="mt-8 text-body text-[color:var(--color-danger)]">
          Couldn&apos;t load the answers.{" "}
          {friendlyError(
            query.error,
            "Check your internet connection and try again.",
          )}
        </p>
      ) : null}

      {summary ? (
        summary.people === 0 ? (
          <p className="empty-state">No answers yet.</p>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap gap-3">
              <StatPlate value={summary.people} label="People answered" />
              <StatPlate
                value={summary.online.Yes + summary.online.Maybe}
                label="Would join online"
                tone="amber"
              />
              <StatPlate
                value={summary.again["Yes, tell me when"]}
                label="Would come again"
              />
            </div>

            <div className="mt-8">
              <Panel title="When people could meditate online">
                <p className="text-body text-[color:var(--color-ink-soft)]">
                  How many of the {interested.length} people who said yes or
                  maybe ticked each day and time (Sydney time).
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full min-w-[34rem] border-collapse text-body tabular-nums">
                    <thead>
                      <tr>
                        <th className="table-head py-2 pr-3">Time</th>
                        {ONLINE_DAYS.map((d) => (
                          <th
                            key={d}
                            className="table-head px-1 py-2 text-center"
                          >
                            {d}
                          </th>
                        ))}
                        <th className="table-head py-2 pl-3 text-right">
                          Any day
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ONLINE_TIME_IDS.map((t) => (
                        <tr
                          key={t}
                          className="border-t border-[color:var(--color-line)]"
                        >
                          <th
                            scope="row"
                            className="py-2 pr-3 text-left font-normal text-[color:var(--color-ink)]"
                          >
                            {ONLINE_TIME_LABEL[t].label}
                            <span className="block text-label text-[color:var(--color-ink-soft)]">
                              {ONLINE_TIME_LABEL[t].hours}
                            </span>
                          </th>
                          {ONLINE_DAYS.map((d) => {
                            const n = summary.grid[d]?.[t] ?? 0;
                            return (
                              <td key={d} className="px-1 py-1.5 text-center">
                                <span
                                  className="inline-flex size-10 items-center justify-center rounded-md font-semibold"
                                  style={{
                                    backgroundColor:
                                      n > 0
                                        ? `color-mix(in srgb, var(--color-amber) ${Math.round(12 + (n / gridMax) * 60)}%, transparent)`
                                        : "transparent",
                                    color:
                                      n > 0
                                        ? "var(--color-ink)"
                                        : "var(--color-ink-faint)",
                                  }}
                                >
                                  {n}
                                </span>
                              </td>
                            );
                          })}
                          <td className="py-2 pl-3 text-right font-semibold text-[color:var(--color-ink)]">
                            {summary.times[t]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <Panel title="How it was">
                <ul className="space-y-3">
                  {FEEDBACK_FEEL.map((f) => (
                    <CountRow
                      key={f}
                      label={f}
                      count={summary.feel[f]}
                      of={summary.people}
                    />
                  ))}
                </ul>
              </Panel>
              <Panel title="Coming again, and online">
                <ul className="space-y-3">
                  {FEEDBACK_AGAIN.map((a) => (
                    <CountRow
                      key={a}
                      label={`Come again: ${a}`}
                      count={summary.again[a]}
                      of={summary.people}
                    />
                  ))}
                  {ONLINE_INTEREST.map((o) => (
                    <CountRow
                      key={o}
                      label={`Online: ${o}`}
                      count={summary.online[o]}
                      of={summary.people}
                    />
                  ))}
                </ul>
              </Panel>
              <Panel title="Which events they came to">
                <ul className="space-y-3">
                  {[...EVENT_LABEL].map(([slug, label]) => (
                    <CountRow
                      key={slug}
                      label={label}
                      count={summary.events[slug] ?? 0}
                      of={summary.people}
                    />
                  ))}
                </ul>
              </Panel>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Search names, emails and answers"
              />
              {interested.length > 0 ? (
                <button
                  type="button"
                  onClick={() => void copyInterested()}
                  className={buttonClass}
                >
                  {copied === "ok" ? (
                    <Check size={16} aria-hidden="true" />
                  ) : (
                    <Copy size={16} aria-hidden="true" />
                  )}
                  {copied === "ok"
                    ? "Copied"
                    : `Copy emails of the ${interested.length} interested online`}
                </button>
              ) : null}
              <button type="button" onClick={download} className={buttonClass}>
                <Download size={16} aria-hidden="true" />
                Download spreadsheet
              </button>
            </div>
            {copied === "failed" ? (
              <p
                role="alert"
                className="mt-3 text-body text-[color:var(--color-danger)]"
              >
                Couldn&apos;t copy. Download the spreadsheet instead.
              </p>
            ) : null}

            <ul className="mt-6 space-y-4">
              {visible.length === 0 ? (
                <li className="empty-state">Nothing matches that search.</li>
              ) : null}
              {visible.map((r) => (
                <li key={r.id} className="surface p-5 md:p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <p className="font-display text-lead text-[color:var(--color-ink)]">
                      {r.name}
                    </p>
                    <p className="text-label text-[color:var(--color-ink-soft)] tabular-nums">
                      {formatDate(r.createdAt)}
                      {r.superseded ? " · they answered again later" : ""}
                    </p>
                  </div>
                  <a
                    href={`mailto:${r.email}`}
                    className="text-body text-[color:var(--color-amber)] underline-offset-4 hover:underline"
                  >
                    {r.email}
                  </a>
                  <dl className="mt-4 grid gap-x-6 gap-y-3 text-body md:grid-cols-[12rem_minmax(0,1fr)]">
                    {(
                      [
                        [
                          "Came to",
                          r.answers.events
                            .map((e) => EVENT_LABEL.get(e) ?? e)
                            .join("; "),
                        ],
                        ["How it was", r.answers.feel ?? ""],
                        ["What stayed with them", r.answers.stayed],
                        ["What we could do better", r.answers.better],
                        ["Come again", r.answers.again ?? ""],
                        [
                          "Online meditation",
                          r.answers.online
                            ? [
                                r.answers.online,
                                wantsOnline(r) ? onlineWhen(r) : "",
                              ]
                                .filter(Boolean)
                                .join(" · ")
                            : "",
                        ],
                        ["Anything else", r.answers.more],
                      ] satisfies [string, string][]
                    )
                      .filter(([, value]) => value)
                      .map(([term, value]) => (
                        <div key={term} className="contents">
                          <dt className="font-semibold text-[color:var(--color-ink-soft)]">
                            {term}
                          </dt>
                          <dd className="whitespace-pre-wrap text-[color:var(--color-ink)]">
                            {value}
                          </dd>
                        </div>
                      ))}
                  </dl>
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
    </section>
  );
}
